import { Box, Button, ButtonBase, keyframes, Paper, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
const pulse = keyframes `
  0%, 100% { opacity: 1; }
  50% { opacity: 0.9; }
`;
const makeGlow = (color) => keyframes `
  0%, 100% { box-shadow: 0 4px 14px ${alpha(color, 0.4)}; }
  50% { box-shadow: 0 4px 20px ${alpha(color, 0.6)}; }
`;
const CARD_BORDER_RADIUS = 12;
const CARD_BORDER_WIDTH = 2;
const CARD_INNER_RADIUS = CARD_BORDER_RADIUS - CARD_BORDER_WIDTH;
export const PlanCardWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'mostPopular' && prop !== 'branding',
})(({ selected, mostPopular, branding }) => {
    const primary = alpha(branding.primaryColor, 1);
    const secondary = alpha(branding.secondaryColor, 1);
    const grey300 = branding.grey300;
    const getBorderColor = () => {
        if (selected)
            return mostPopular ? primary : secondary;
        return mostPopular ? primary : grey300;
    };
    const getBackground = () => {
        if (selected) {
            return mostPopular
                ? `linear-gradient(to bottom right, ${primary}, ${primary})`
                : `linear-gradient(to bottom right, ${secondary}, ${secondary})`;
        }
        return '#ffffff';
    };
    const getBoxShadow = () => {
        if (selected) {
            return mostPopular
                ? `0 4px 12px ${alpha(branding.primaryColor, 0.2)}`
                : `0 4px 12px ${alpha(branding.secondaryColor, 0.2)}`;
        }
        return 'none';
    };
    const getHoverBorder = () => {
        if (selected)
            return undefined;
        return mostPopular ? primary : grey300;
    };
    const getHoverShadow = () => {
        if (selected) {
            return mostPopular
                ? `0 8px 20px ${alpha(branding.primaryColor, 0.3)}`
                : `0 8px 20px ${alpha(branding.secondaryColor, 0.3)}`;
        }
        return '0 4px 12px rgba(0,0,0,0.08)';
    };
    const getHoverScale = () => (selected ? 'scale(1.03)' : 'scale(1.01)');
    return {
        position: 'relative',
        borderRadius: CARD_BORDER_RADIUS,
        border: `${CARD_BORDER_WIDTH}px solid ${getBorderColor()}`,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        background: getBackground(),
        color: selected ? '#ffffff' : '#111827',
        transform: selected ? 'scale(1.02)' : 'scale(1)',
        boxShadow: getBoxShadow(),
        cursor: 'pointer',
        '&:hover': {
            borderColor: getHoverBorder(),
            boxShadow: getHoverShadow(),
            transform: getHoverScale(),
        },
    };
});
export const PlanCardButton = styled(ButtonBase)({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderRadius: CARD_INNER_RADIUS,
    textAlign: 'left',
});
export const MostPopularBadge = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'branding',
})(({ selected, branding }) => {
    const primary = alpha(branding.primaryColor, 1);
    const secondary = alpha(branding.secondaryColor, 1);
    const background = selected
        ? `linear-gradient(to right, ${secondary}, ${secondary})`
        : `linear-gradient(to right, ${primary}, ${primary})`;
    return {
        position: 'absolute',
        top: -10,
        right: -10,
        padding: '4px 12px',
        borderRadius: 9999,
        fontSize: '0.75rem',
        fontWeight: 600,
        background,
        color: '#ffffff',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        animation: selected ? 'none' : `${pulse} 2s ease-in-out infinite`,
    };
});
export const StyledPaper = styled(Paper)({
    borderRadius: 16,
    padding: '48px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.04)',
    maxWidth: 1200,
    width: '100%',
    '@media (max-width: 900px)': { padding: '32px' },
    '@media (max-width: 600px)': { padding: '24px' },
});
export const FeatureBox = styled(Box)({
    background: 'linear-gradient(to bottom right, #f9fafb, #ffffff)',
    borderRadius: 12,
    padding: '24px',
    border: '1px solid #f3f4f6',
    '@media (max-width: 900px)': { padding: '20px' },
});
export const ContinueButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'branding',
})(({ branding }) => {
    const primary = alpha(branding.primaryColor, 1);
    return {
        background: `linear-gradient(to right, ${primary}, ${primary})`,
        color: '#ffffff',
        padding: '12px 32px',
        borderRadius: 8,
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: 500,
        boxShadow: `0 4px 14px ${alpha(branding.primaryColor, 0.4)}`,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: `${makeGlow(branding.primaryColor)} 2s ease-in-out infinite`,
        cursor: 'pointer',
        '&:hover': {
            background: `linear-gradient(to right, ${primary}, ${primary})`,
            boxShadow: `0 6px 20px ${alpha(branding.primaryColor, 0.5)}`,
            transform: 'translateY(-1px) scale(1.02)',
            animation: 'none',
        },
        '&:active': {
            transform: 'translateY(0) scale(0.98)',
        },
    };
});
